/**
 * 路由守卫组件
 * 处理需要鉴权的路由访问控制
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { AUTH_ROUTES } from '../../../config/routes';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requireAuth = true, 
  redirectTo = AUTH_ROUTES.LOGIN 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    // 如果还在加载中，不进行跳转
    if (loading) return;

    // 如果需要认证但用户未登录
    if (requireAuth && !isAuthenticated) {
      // 保存当前路径，登录后可以跳转回来
      const currentPath = location.pathname + location.search;
      navigate(`${redirectTo}?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }

    // 如果用户已登录但访问登录页面，跳转到数据管理页面
    if (isAuthenticated && location.pathname === AUTH_ROUTES.LOGIN) {
      navigate(AUTH_ROUTES.DATA_MANAGEMENT);
      return;
    }
  }, [isAuthenticated, loading, requireAuth, navigate, location, redirectTo]);

  // 如果还在加载中，显示加载状态
  if (loading) {
    return (
      <div className="auth-guard-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>正在验证身份...</p>
        </div>
      </div>
    );
  }

  // 如果需要认证但用户未登录，不渲染内容
  if (requireAuth && !isAuthenticated) {
    return null;
  }

  // 如果用户已登录但访问登录页面，不渲染内容
  if (isAuthenticated && location.pathname === AUTH_ROUTES.LOGIN) {
    return null;
  }

  return <>{children}</>;
};

export default AuthGuard;
